import React, { useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { map, prop, reverse, slice, sortBy } from "ramda";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { notBlank } from "../../util/ext";
import { DateTime } from "luxon";
import Tooltip from "@mui/material/Tooltip";
import { Link, useResolvedPath } from "react-router-dom";
import CustomTablePagination from "../../creator-application/common/CustomTablePagination";

const DEFAULT_ROWS_PER_PAGE = 20;

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 400px)",
    overflow: "auto",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const ContentIngestionJobTable = props => {
  const {
    metadataFileDiscoveryList,
    page,
    onPageChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const url = useResolvedPath(".").pathname;
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const handleRowsPerPageChange = event => {
    console.log(event);
    setRowsPerPage(event.target.value);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Metadata File Discovery">
          <TableHead>
            <TableRow>
              <TableCell
                key="eventTimestamp"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Timestamp
              </TableCell>

              <TableCell
                key="jobId"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Job Id
              </TableCell>

              <TableCell
                key="metadataFileLocation"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Metadata File Location
              </TableCell>

              <TableCell
                key="jobStatus"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {map(
              mfd => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={
                    mfd.jobId + DateTime.fromISO(mfd.eventTimestamp).toMillis()
                  }
                >
                  <TableCell
                    key={"eventTimestamp:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {DateTime.fromISO(mfd.eventTimestamp)
                      .toUTC()
                      .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                  </TableCell>

                  <TableCell
                    key={"jobId:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    <Tooltip title="View Event">
                      <Link className={classes.link} to={`${url}/${mfd.jobId}`}>
                        {mfd.jobId}
                      </Link>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    key={"metadataFileLocation:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {mfd.filename}
                  </TableCell>

                  <TableCell
                    key={"jobStatus:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, textTransform: "capitalize" }}
                  >
                    {mfd.jobStatus}
                  </TableCell>
                </TableRow>
              ),
              slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
                reverse(
                  sortBy(prop("eventTimestamp"), metadataFileDiscoveryList),
                ),
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        classes={{
          root: classes.pagination,
        }}
        colSpan={4}
        component="div"
        count={metadataFileDiscoveryList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Paper>
  );
};

export default ContentIngestionJobTable;
